import { useState, useRef, useCallback } from 'react';
import { useScrollDirection } from 'use-scroll-direction';

interface Section {
  top: number;
  coverage: number;
  yPosToDocument: number;
  isIntersecting: boolean;
  intersectionRatio: number;
}

type SectionEntry = [string, Section];

const filterSections =
  (keys: string[]) =>
  ([k, v]: SectionEntry) => {
    if (!keys.includes(k)) return false; // Include only the provided sections
    if (!v.isIntersecting) return false;
    if (v.coverage < 0.02) return false;
    return true;
  };

const sortYPosDesc = ([, a]: SectionEntry, [, b]: SectionEntry) =>
  b.yPosToDocument - a.yPosToDocument;

const sortCoverageDesc = ([, a]: SectionEntry, [, b]: SectionEntry) =>
  b.coverage - a.coverage;

/**
 * Choosing the current section is complex and depends on the direction of the
 * user is scrolling.
 * When scrolling down, we want to select the next section
 * sooner as that is the section the user is likely to be interested in and
 * gives us a chance to preload the content.
 * When scrolling up, we want to select the section that consumes the most of
 * the screen. We don't use the same logic, as the section would change when
 * only the bottom of the section is showing = not enough shown to switch.
 * Note: When testing, you'll likely see slightly different "section choosing"
 * results as there's various throttling, thresholds and caching on top of this.
 * Even the speed of your scroll will yield slightly different results.
 */
export const getSelectedSection = (
  keys,
  entities,
  isScrollingUp = false
): string => {
  const items = Object.entries<Section>(entities);
  const results = items.filter(filterSections(keys));

  if (results.length === 0) return '';

  // When scrolling up, we match the section with the most coverage
  if (isScrollingUp) return results.sort(sortCoverageDesc)[0][0];

  // When scrolling down, we match the section at the bottom of the page
  return results.sort(sortYPosDesc)[0][0];
};

export type IntersectionEntities = Record<string, Section>;

export interface IntersectionPayload {
  keys: string[];
  entities: IntersectionEntities;
}

/** Imported from @components/IntersectionListener */

export function useIntersectionListener(
  options: {
    onIntersectionUpdate?: (
      data: IntersectionPayload,
      targetSelection?: string
    ) => void;
    disabled?: boolean;
  } = {}
) {
  const { onIntersectionUpdate, disabled } = options;
  const [selected, setSelected] = useState('');
  const intersectorsKeys = useRef<string[]>([]);
  const intersectionEntities = useRef<IntersectionEntities>({});

  const { isScrollingDown } = useScrollDirection({ timeToReset: 750 });

  const handleIntersectionUpdate = useCallback(
    (data: IntersectionPayload, targetSelection: string) => {
      if (targetSelection) {
        setSelected(targetSelection);
      }
      onIntersectionUpdate && onIntersectionUpdate(data, targetSelection);
    },
    [onIntersectionUpdate]
  );
  return {
    selected,
    handleIntersectionChange: useCallback(
      (ioEntry: IntersectionObserverEntry, anchor: string) => {
        if (disabled) return;
        const {
          isIntersecting,
          intersectionRatio,
          boundingClientRect: { top },
          intersectionRect: { height },
        } = ioEntry;
        //
        if (anchor.length === 0) return;
        //
        let keys: string[] = intersectorsKeys.current;
        let entities: IntersectionEntities = {};
        //
        const index = keys.findIndex((n) => n === anchor);
        // Is the target of the event intersecting
        if (index > -1 && !isIntersecting) {
          // No? Remove it for the intersectors list
          keys = [...keys.slice(0, index), ...keys.slice(index + 1)];
        } else if (index === -1 && isIntersecting) {
          // Yes? Add it to the intersectors list
          keys.push(anchor);
        }

        if (RUNTIME_ENV !== 'server' && typeof document !== 'undefined') {
          const body = document.body;
          const documentElement = document.documentElement;
          let scrollTop = 0;
          try {
            scrollTop =
              window.pageYOffset || documentElement.scrollTop || body.scrollTop;
          } catch {
            scrollTop = 0;
          }
          const clientTop = documentElement?.clientTop || body?.clientTop || 0;

          entities = {
            ...intersectionEntities.current,
            [anchor]: {
              top,
              coverage: height / window.innerHeight,
              isIntersecting,
              intersectionRatio,
              yPosToDocument: top + scrollTop - clientTop,
            },
          };
          //
          const selected = getSelectedSection(keys, entities, !isScrollingDown);
          //
          handleIntersectionUpdate(
            {
              keys,
              entities,
            },
            selected
          );
          // Cache intersectors
          intersectorsKeys.current = keys;
          intersectionEntities.current = entities;
        }
      },
      [disabled, isScrollingDown, handleIntersectionUpdate]
    ),
  };
}
